// pages/Admin/NotificationsPage.jsx
import React, { useState, useEffect } from 'react';
import { FiBell, FiCheck, FiTrash2, FiInfo, FiCheckCircle, FiAlertTriangle, FiAlertCircle, FiFilter, FiRefreshCw } from 'react-icons/fi';
import notificationService from '../../services/notificationService';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, info, success, warning, error
  const [moduleFilter, setModuleFilter] = useState('all');
  
  // Kullanıcı rolünü kontrol et - .env'den gelen süper admin
  const userRole = localStorage.getItem('adminRole'); // DÜZELTME: 'adminRole' key'ini kullan
  const isSuperAdmin = userRole === 'superadmin' || userRole === 'super_admin' || userRole === 'SUPERADMIN';
  
  // Debug: Role kontrolü
  console.log('🔴 NotificationsPage - Current Role:', userRole);
  console.log('🔴 NotificationsPage - Is SuperAdmin:', isSuperAdmin);

  // Bildirimleri yükle
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filter === 'unread') params.is_read = false;
      if (filter === 'read') params.is_read = true;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (moduleFilter !== 'all') params.module = moduleFilter;
      
      const response = await notificationService.getNotifications(params);
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter, typeFilter, moduleFilter]);

  // Bildirimi okundu olarak işaretle
  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Tümünü okundu olarak işaretle
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Bildirimi sil
  const handleDelete = async (id) => {
    if (!window.confirm('Bu bildirimi silmek istediğinize emin misiniz?')) return;
    
    try {
      await notificationService.deleteNotification(id);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Eski bildirimleri temizle
  const handleClearOld = async () => {
    if (!window.confirm('30 günden eski tüm bildirimler silinecek. Emin misiniz?')) return;
    
    try {
      const response = await notificationService.clearOldNotifications();
      alert(response.message);
      fetchNotifications();
    } catch (error) {
      console.error('Error clearing old notifications:', error);
    }
  };

  // Tip ikonunu getir
  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle className="text-green-500" />;
      case 'warning':
        return <FiAlertTriangle className="text-yellow-500" />;
      case 'error':
        return <FiAlertCircle className="text-red-500" />;
      default:
        return <FiInfo className="text-blue-500" />;
    }
  };

  // Tip rengini getir
  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  // Modül isimlerini Türkçeleştir
  const getModuleName = (module) => {
    const modules = {
      'ogrenci': 'Öğrenci',
      'ogretmen': 'Öğretmen',
      'ders': 'Ders',
      'odeme': 'Ödeme',
      'kurssatis': 'Kurs Satış',
      'system': 'Sistem',
      'email': 'E-posta',
      'sms': 'SMS'
    };
    return modules[module] || module;
  };

  // Tarih formatla
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Unique modülleri al
  const uniqueModules = [...new Set(notifications.map(n => n.module))];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <FiBell className="mr-3" />
              Bildirimler
            </h1>
            <p className="text-gray-600 mt-2">Sistem bildirimlerini yönetin</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchNotifications}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Yenile
            </button>
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <FiCheck className="mr-2" />
              Tümünü Okundu İşaretle
            </button>
            {isSuperAdmin && (
              <button
                onClick={handleClearOld}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <FiTrash2 className="mr-2" />
                Eski Bildirimleri Sil
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <FiFilter className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtreler:</span>
          </div>
          
          {/* Durum Filtresi */}
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tümü
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'unread' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Okunmamış
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'read' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Okunmuş
            </button>
          </div>

          {/* Tip Filtresi */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Tipler</option>
            <option value="info">Bilgi</option>
            <option value="success">Başarı</option>
            <option value="warning">Uyarı</option>
            <option value="error">Hata</option>
          </select>

          {/* Modül Filtresi */}
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Modüller</option>
            {uniqueModules.map(module => (
              <option key={module} value={module}>
                {getModuleName(module)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Yükleniyor...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <FiBell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Bildirim Bulunamadı</h3>
            <p className="text-gray-500">Seçili filtrelere uygun bildirim yok</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-6 hover:bg-gray-50 transition-colors ${
                  !notification.is_read ? 'bg-blue-50/30' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="mt-1">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            Yeni
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3">
                        {notification.message}
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={`px-2 py-1 rounded border ${getTypeColor(notification.type)}`}>
                          {notification.type}
                        </span>
                        <span className="text-gray-500">
                          Modül: <span className="font-medium">{getModuleName(notification.module)}</span>
                        </span>
                        {/* Admin Bilgisi */}
                        {notification.admin_name && (
                          <span className="text-gray-500">
                            {notification.admin_type === 'super_admin' ? '👑 ' : '👤 '}
                            <span className="font-medium">
                              {notification.admin_type === 'super_admin'
                                ? `Süper Admin (${notification.admin_name})`
                                : `Admin (${notification.admin_name})`
                              }
                            </span>
                          </span>
                        )}
                        <span className="text-gray-500">
                          {formatDate(notification.created_at)}
                        </span>
                        {notification.read_at && (
                          <span className="text-gray-400">
                            Okunma: {formatDate(notification.read_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!notification.is_read && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Okundu olarak işaretle"
                      >
                        <FiCheck />
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Sil"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* İstatistikler */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Toplam</div>
          <div className="text-2xl font-bold text-gray-900">{notifications.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Okunmamış</div>
          <div className="text-2xl font-bold text-blue-600">
            {notifications.filter(n => !n.is_read).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Bugün</div>
          <div className="text-2xl font-bold text-green-600">
            {notifications.filter(n => 
              new Date(n.created_at).toDateString() === new Date().toDateString()
            ).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Bu Hafta</div>
          <div className="text-2xl font-bold text-purple-600">
            {notifications.filter(n => {
              const date = new Date(n.created_at);
              const now = new Date();
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return date >= weekAgo;
            }).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
